/**
 * VANI - Luxury Jewelry Website
 * Global JavaScript
 * --------------------------------
 * Modules:
 *  1. Scroll Reveal
 *  2. Header Scroll State
 *  3. Mobile Menu
 *  4. Nav Dropdowns
 *  5. Counter Animation
 *  6. Custom Cursor
 *  7. Video Hero
 *  8. Marquee Pause
 *  9. Smooth Scroll
 * 10. Announcement Bar Close
 * 11. Search Overlay
 * 12. Newsletter Form
 */

document.addEventListener('DOMContentLoaded', () => {
  'use strict';

  /* ==========================================================================
     1. SCROLL REVEAL
     ========================================================================== */

  const initScrollReveal = () => {
    const revealElements = document.querySelectorAll('[data-reveal], .reveal');
    if (!revealElements.length) return;

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target;

          // Apply delay from data attribute if present
          const delay = el.getAttribute('data-reveal-delay');
          if (delay) {
            el.style.transitionDelay = delay;
          }

          el.classList.add('is-visible');
          revealObserver.unobserve(el);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    revealElements.forEach((el) => revealObserver.observe(el));

    // Stagger children: each child inside .stagger-children gets incremental delay
    const staggerContainers = document.querySelectorAll('.stagger-children');
    staggerContainers.forEach((container) => {
      const children = Array.from(container.children);

      const staggerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            children.forEach((child, index) => {
              child.style.transitionDelay = `${index * 0.1}s`;
              child.classList.add('is-visible');
            });

            staggerObserver.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -40px 0px',
        }
      );

      staggerObserver.observe(container);
    });
  };

  /* ==========================================================================
     2. HEADER SCROLL STATE
     ========================================================================== */

  const initHeaderScroll = () => {
    const header = document.querySelector('.header');
    if (!header) return;

    // Create an invisible sentinel at the very top of the page
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);

    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          header.classList.remove('is-scrolled');
        } else {
          header.classList.add('is-scrolled');
        }
      },
      { threshold: 0 }
    );

    headerObserver.observe(sentinel);
  };

  /* ==========================================================================
     3. MOBILE MENU
     ========================================================================== */

  const initMobileMenu = () => {
    const hamburger = document.querySelector('.hamburger');
    const mobileMenu = document.querySelector('.mobile-menu');
    const overlay = document.querySelector('.mobile-menu__overlay');

    if (!hamburger || !mobileMenu) return;

    /** Calculate scrollbar width to prevent layout jump */
    const getScrollbarWidth = () => window.innerWidth - document.documentElement.clientWidth;

    /** Lock body scroll */
    const lockScroll = () => {
      const scrollbarWidth = getScrollbarWidth();
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    };

    /** Unlock body scroll */
    const unlockScroll = () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };

    /** Get all focusable elements inside the menu */
    const getFocusable = () =>
      mobileMenu.querySelectorAll(
        'a[href], button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );

    /** Open mobile menu */
    const openMenu = () => {
      hamburger.classList.add('is-open');
      mobileMenu.classList.add('is-open');
      overlay?.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      lockScroll();

      // Focus first focusable element inside menu
      const focusable = getFocusable();
      if (focusable.length) focusable[0].focus();
    };

    /** Close mobile menu */
    const closeMenu = () => {
      hamburger.classList.remove('is-open');
      mobileMenu.classList.remove('is-open');
      overlay?.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      unlockScroll();
      hamburger.focus();
    };

    /** Toggle menu */
    const toggleMenu = () => {
      if (mobileMenu.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    hamburger.addEventListener('click', toggleMenu);
    overlay?.addEventListener('click', closeMenu);

    // ESC to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    // Focus trap
    mobileMenu.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;

      const focusable = getFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    // Expandable sub-menu items
    const subToggles = document.querySelectorAll('.mobile-menu__sub-toggle');
    subToggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const parent = toggle.parentElement;
        const subList = parent?.querySelector('.mobile-menu__sub-items');

        if (!parent || !subList) return;

        const isExpanded = parent.classList.contains('is-expanded');

        if (isExpanded) {
          // Collapse: animate max-height to 0
          subList.style.maxHeight = `${subList.scrollHeight}px`;
          // Force reflow so browser recognises the starting value
          void subList.offsetHeight;
          subList.style.maxHeight = '0px';
          parent.classList.remove('is-expanded');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          // Expand: animate max-height from 0 to scrollHeight
          subList.style.maxHeight = '0px';
          parent.classList.add('is-expanded');
          toggle.setAttribute('aria-expanded', 'true');
          subList.style.maxHeight = `${subList.scrollHeight}px`;

          // After transition, remove inline max-height so content can resize
          const onEnd = () => {
            subList.style.maxHeight = 'none';
            subList.removeEventListener('transitionend', onEnd);
          };
          subList.addEventListener('transitionend', onEnd);
        }
      });
    });
  };

  /* ==========================================================================
     4. NAV DROPDOWNS (Desktop hover-intent + keyboard)
     ========================================================================== */

  const initNavDropdowns = () => {
    const dropdownItems = document.querySelectorAll('.has-dropdown');
    if (!dropdownItems.length) return;

    const HOVER_DELAY = 100; // ms

    dropdownItems.forEach((item) => {
      const dropdown = item.querySelector('.nav-dropdown');
      if (!dropdown) return;

      let showTimer = null;
      let hideTimer = null;

      const show = () => {
        clearTimeout(hideTimer);
        showTimer = setTimeout(() => {
          item.classList.add('is-open');
          dropdown.setAttribute('aria-hidden', 'false');
        }, HOVER_DELAY);
      };

      const hide = () => {
        clearTimeout(showTimer);
        hideTimer = setTimeout(() => {
          item.classList.remove('is-open');
          dropdown.setAttribute('aria-hidden', 'true');
        }, HOVER_DELAY);
      };

      const close = () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
        item.classList.remove('is-open');
        dropdown.setAttribute('aria-hidden', 'true');
      };

      // Mouse intent
      item.addEventListener('mouseenter', show);
      item.addEventListener('mouseleave', hide);

      // Keyboard: trigger link or toggle
      const trigger = item.querySelector('.nav-dropdown__trigger, > a, > button');
      trigger?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const isOpen = item.classList.contains('is-open');
          if (isOpen) {
            close();
          } else {
            clearTimeout(hideTimer);
            clearTimeout(showTimer);
            item.classList.add('is-open');
            dropdown.setAttribute('aria-hidden', 'false');
            // Focus first link inside dropdown
            const firstLink = dropdown.querySelector('a, button');
            firstLink?.focus();
          }
        }

        if (e.key === 'Escape') {
          close();
          trigger.focus();
        }
      });

      // Arrow key navigation within dropdown
      dropdown.addEventListener('keydown', (e) => {
        const links = Array.from(dropdown.querySelectorAll('a, button'));
        const currentIndex = links.indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const next = links[currentIndex + 1] || links[0];
          next?.focus();
        }

        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prev = links[currentIndex - 1] || links[links.length - 1];
          prev?.focus();
        }

        if (e.key === 'Escape') {
          close();
          trigger?.focus();
        }
      });
    });

    // Close all dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.has-dropdown')) {
        dropdownItems.forEach((item) => {
          item.classList.remove('is-open');
          const dd = item.querySelector('.nav-dropdown');
          dd?.setAttribute('aria-hidden', 'true');
        });
      }
    });
  };

  /* ==========================================================================
     5. COUNTER ANIMATION
     ========================================================================== */

  const initCounters = () => {
    const counters = document.querySelectorAll('.stat-card__number');
    if (!counters.length) return;

    /** Ease-out cubic: decelerates towards end */
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    const animateCounter = (el) => {
      const target = el.getAttribute('data-target');
      const suffix = el.getAttribute('data-suffix') || '';

      // Special case: infinity symbol
      if (target === '\u221E' || target === 'infinity' || target === '∞') {
        el.textContent = '∞';
        el.classList.add('is-visible');
        return;
      }

      const targetNum = parseFloat(target);
      if (isNaN(targetNum)) return;

      const duration = 1800; // 1.8s
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        const current = Math.round(easedProgress * targetNum);

        el.textContent = current.toLocaleString() + suffix;

        if (progress < 1) {
          requestAnimationFrame(tick);
        } else {
          // Ensure final value is exact
          el.textContent = targetNum.toLocaleString() + suffix;
        }
      };

      requestAnimationFrame(tick);
    };

    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.2 }
    );

    counters.forEach((el) => counterObserver.observe(el));
  };

  /* ==========================================================================
     6. CUSTOM CURSOR
     ========================================================================== */

  const initCustomCursor = () => {
    // Detect touch device -- skip cursor entirely
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    // Create cursor elements
    const dot = document.createElement('div');
    dot.className = 'cursor-dot';
    const ring = document.createElement('div');
    ring.className = 'cursor-ring';
    document.body.appendChild(dot);
    document.body.appendChild(ring);
    document.body.classList.add('has-cursor');

    let mouseX = -100;
    let mouseY = -100;
    let ringX = -100;
    let ringY = -100;
    const LERP = 0.12;
    let cursorVisible = true;

    // Track mouse position
    document.addEventListener('mousemove', (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      // Instant dot follow
      dot.style.transform = `translate(${mouseX}px, ${mouseY}px)`;

      if (!cursorVisible) {
        dot.style.opacity = '1';
        ring.style.opacity = '1';
        cursorVisible = true;
      }
    });

    // Ring follows with lerp via rAF
    const animateRing = () => {
      ringX += (mouseX - ringX) * LERP;
      ringY += (mouseY - ringY) * LERP;
      ring.style.transform = `translate(${ringX}px, ${ringY}px)`;
      requestAnimationFrame(animateRing);
    };
    requestAnimationFrame(animateRing);

    // Hover states for interactive elements
    const addHoverListeners = (el) => {
      el.addEventListener('mouseenter', () => {
        dot.classList.add('is-hover');
        ring.classList.add('is-hover');
      });
      el.addEventListener('mouseleave', () => {
        dot.classList.remove('is-hover');
        ring.classList.remove('is-hover');
      });
    };

    document
      .querySelectorAll('a, button, [data-hover], input, textarea, select')
      .forEach(addHoverListeners);

    // Product card hover: gold variant
    document.querySelectorAll('.product-card__media').forEach((card) => {
      card.addEventListener('mouseenter', () => dot.classList.add('is-product'));
      card.addEventListener('mouseleave', () => dot.classList.remove('is-product'));
    });

    // Hide cursor on touch events (hybrid devices)
    const hideCursor = () => {
      dot.style.opacity = '0';
      ring.style.opacity = '0';
      cursorVisible = false;
    };

    window.addEventListener('touchstart', hideCursor, { passive: true });

    // Handle dynamically added interactive elements via MutationObserver
    const cursorMo = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches?.('a, button, [data-hover], input, textarea, select')) {
            addHoverListeners(node);
          }
          node.querySelectorAll?.('a, button, [data-hover], input, textarea, select').forEach(addHoverListeners);
        });
      });
    });

    cursorMo.observe(document.body, { childList: true, subtree: true });
  };

  /* ==========================================================================
     7. VIDEO HERO
     ========================================================================== */

  const initVideoHero = () => {
    const video = document.querySelector('.hero__video');
    if (!video) return;

    const fallback = document.querySelector('.hero__svg-fallback');
    const controls = document.querySelector('.hero__controls');
    const playBtn = document.querySelector('.hero__play-btn');
    const muteBtn = document.querySelector('.hero__mute-btn');
    const progress = document.querySelector('.hero__progress');
    const offlineBadge = document.querySelector('.hero__offline-badge');

    // --- Reduced motion: pause video ---
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (prefersReducedMotion.matches) {
      video.pause();
    }

    // Listen for changes (user toggles preference)
    prefersReducedMotion.addEventListener('change', (e) => {
      if (e.matches) {
        video.pause();
      }
    });

    // --- canplay: fade in video, fade out fallback ---
    video.addEventListener('canplay', () => {
      video.classList.add('is-loaded');
      fallback?.classList.add('is-hidden');
    });

    // --- error: hide controls, show offline badge ---
    video.addEventListener('error', () => {
      controls?.style.setProperty('display', 'none');
      offlineBadge?.classList.add('is-visible');
    });

    // --- Online / Offline events ---
    window.addEventListener('online', () => {
      offlineBadge?.classList.remove('is-visible');
    });

    window.addEventListener('offline', () => {
      offlineBadge?.classList.add('is-visible');
    });

    // --- Page Visibility API: pause when hidden, resume when visible ---
    document.addEventListener('visibilitychange', () => {
      if (!video) return;
      if (document.hidden) {
        video.pause();
      } else if (!prefersReducedMotion.matches) {
        video.play().catch(() => {});
      }
    });

    // --- Helper to update play button icon ---
    const updatePlayIcon = () => {
      if (!playBtn) return;
      const icon = playBtn.querySelector('.icon, svg, i');
      if (video.paused) {
        playBtn.setAttribute('aria-label', 'Play video');
        playBtn.classList.remove('is-playing');
        playBtn.classList.add('is-paused');
      } else {
        playBtn.setAttribute('aria-label', 'Pause video');
        playBtn.classList.remove('is-paused');
        playBtn.classList.add('is-playing');
      }
    };

    // --- Helper to update mute button icon ---
    const updateMuteIcon = () => {
      if (!muteBtn) return;
      if (video.muted) {
        muteBtn.setAttribute('aria-label', 'Unmute video');
        muteBtn.classList.add('is-muted');
        muteBtn.classList.remove('is-unmuted');
      } else {
        muteBtn.setAttribute('aria-label', 'Mute video');
        muteBtn.classList.remove('is-muted');
        muteBtn.classList.add('is-unmuted');
      }
    };

    // --- Toggle play/pause ---
    const togglePlay = () => {
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
      updatePlayIcon();
    };

    // --- Toggle mute ---
    const toggleMute = () => {
      video.muted = !video.muted;
      updateMuteIcon();
    };

    // --- Play/Pause button ---
    playBtn?.addEventListener('click', togglePlay);

    // --- Mute button ---
    muteBtn?.addEventListener('click', toggleMute);

    // --- Progress bar ---
    if (progress) {
      video.addEventListener('timeupdate', () => {
        if (!video.duration) return;
        const pct = (video.currentTime / video.duration) * 100;
        progress.style.width = `${pct}%`;
      });
    }

    // Sync icons on play/pause events
    video.addEventListener('play', updatePlayIcon);
    video.addEventListener('pause', updatePlayIcon);

    // --- Keyboard controls (only when hero area is in focus scope) ---
    document.addEventListener('keydown', (e) => {
      // Only respond if no input/textarea is focused
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      // Don't conflict with search overlay or other modals
      if (document.querySelector('.search-overlay.is-open')) return;

      if (e.key === ' ' || e.key === 'k' || e.key === 'K') {
        // Prevent space from scrolling page
        if (e.key === ' ') e.preventDefault();
        togglePlay();
      }

      if (e.key === 'm' || e.key === 'M') {
        toggleMute();
      }
    });
  };

  /* ==========================================================================
     8. MARQUEE PAUSE
     ========================================================================== */

  const initMarqueePause = () => {
    const marquees = document.querySelectorAll('.marquee');
    if (!marquees.length) return;

    marquees.forEach((marquee) => {
      marquee.addEventListener('mouseenter', () => marquee.classList.add('is-paused'));
      marquee.addEventListener('mouseleave', () => marquee.classList.remove('is-paused'));
    });
  };

  /* ==========================================================================
     9. SMOOTH SCROLL
     ========================================================================== */

  const initSmoothScroll = () => {
    const HEADER_OFFSET = 72; // px - sticky header height

    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;

      const hash = link.getAttribute('href');
      if (!hash || hash === '#') return;

      const target = document.querySelector(hash);
      if (!target) return;

      e.preventDefault();

      const top = target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });

      // Update URL hash without jumping
      history.pushState(null, '', hash);
    });
  };

  /* ==========================================================================
     10. ANNOUNCEMENT BAR CLOSE
     ========================================================================== */

  const initAnnouncementBar = () => {
    const bar = document.querySelector('.announcement-bar');
    const closeBtn = document.querySelector('.announcement-bar__close');

    if (!bar || !closeBtn) return;

    // Check if already dismissed this session
    if (sessionStorage.getItem('vani_announcement_dismissed') === 'true') {
      bar.style.display = 'none';
      return;
    }

    closeBtn.addEventListener('click', () => {
      bar.style.display = 'none';
      sessionStorage.setItem('vani_announcement_dismissed', 'true');
    });
  };

  /* ==========================================================================
     11. SEARCH OVERLAY
     ========================================================================== */

  const initSearchOverlay = () => {
    const toggle = document.querySelector('.search-toggle');
    if (!toggle) return;

    // Create overlay if it doesn't already exist in markup
    let overlay = document.querySelector('.search-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'search-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-label', 'Search');
      overlay.innerHTML = `
        <div class="search-overlay__inner">
          <button class="search-overlay__close" aria-label="Close search">&times;</button>
          <form class="search-overlay__form" role="search">
            <input
              class="search-overlay__input"
              type="search"
              placeholder="Search VANI..."
              aria-label="Search"
              autocomplete="off"
            />
          </form>
        </div>
      `;
      document.body.appendChild(overlay);
    }

    const input = overlay.querySelector('.search-overlay__input');
    const closeBtn = overlay.querySelector('.search-overlay__close');

    const openSearch = () => {
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      // Focus input after transition
      setTimeout(() => input?.focus(), 100);
    };

    const closeSearch = () => {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      toggle.focus();
    };

    toggle.addEventListener('click', openSearch);
    closeBtn?.addEventListener('click', closeSearch);

    // Close on ESC
    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeSearch();
    });

    // Close when clicking backdrop (outside inner)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeSearch();
    });
  };

  /* ==========================================================================
     12. NEWSLETTER FORM
     ========================================================================== */

  const initNewsletterForm = () => {
    const form = document.querySelector('.email-capture__form');
    if (!form) return;

    // If user already subscribed, hide form and show success
    if (localStorage.getItem('vani_newsletter_subscribed') === 'true') {
      form.style.display = 'none';
      const successMsg = form.parentElement?.querySelector('.email-capture__success');
      if (successMsg) successMsg.style.display = 'block';
      return;
    }

    /** Simple email validation */
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    form.addEventListener('submit', (e) => {
      e.preventDefault();

      const input = form.querySelector('input[type="email"], input[name="email"]');
      if (!input) return;

      const email = input.value.trim();
      if (!isValidEmail(email)) {
        input.classList.add('is-invalid');
        // Show error if a message element exists
        const errorEl = form.querySelector('.email-capture__error');
        if (errorEl) {
          errorEl.textContent = 'Please enter a valid email address.';
          errorEl.style.display = 'block';
        }
        return;
      }

      // Clear any previous error state
      input.classList.remove('is-invalid');
      const errorEl = form.querySelector('.email-capture__error');
      if (errorEl) errorEl.style.display = 'none';

      // Show success state
      form.style.display = 'none';
      const successMsg = form.parentElement?.querySelector('.email-capture__success');
      if (successMsg) {
        successMsg.style.display = 'block';
      } else {
        // Create a simple success message if none exists
        const msg = document.createElement('p');
        msg.className = 'email-capture__success';
        msg.textContent = 'Thank you for subscribing!';
        form.parentElement?.appendChild(msg);
      }

      // Persist to localStorage
      localStorage.setItem('vani_newsletter_subscribed', 'true');
    });
  };

  /* ==========================================================================
     INIT ALL MODULES
     ========================================================================== */

  initScrollReveal();
  initHeaderScroll();
  initMobileMenu();
  initNavDropdowns();
  initCounters();
  initCustomCursor();
  initVideoHero();
  initMarqueePause();
  initSmoothScroll();
  initAnnouncementBar();
  initSearchOverlay();
  initNewsletterForm();
});
